import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { userAPI, otpAPI } from '../../services/api';
import Sidebar from '../../components/Common/Sidebar';
import {
  User, Building2, Phone, MapPin, CreditCard,
  Camera, Save, ShieldCheck, Loader2,
} from 'lucide-react';
import { toast } from 'react-toastify';

/* ── design tokens ── */
const C = {
  bg:          '#f4f4f9',
  surface:     '#FFFFFF',
  border:      '#e8e8f1',
  borderSoft:  '#f0f0f6',
  ink:         '#14142b',
  body:        '#4a4a6a',
  muted:       '#6e6e86',
  primary:     '#7c3aed',
  primarySoft: '#f1ebff',
  primaryTint: '#ddd6fe',
  success:     '#10B981',
  successSoft: '#D1FAE5',
  warning:     '#F59E0B',
  warnSoft:    '#FEF3C7',
  danger:      '#EF4444',
  dangerSoft:  '#FEE2E2',
  radius:      14,
  radiusSm:    8,
};

/* ── Agreement text ── */
const AGREEMENT_TEXT = `A1XPAY MERCHANT AGREEMENT, TERMS & CONDITIONS
Effective Version: v1.0

This Merchant Agreement ("Agreement") is entered into between A1XPAY Private Limited ("A1XPAY", "We", "Us", or "Our") and the Merchant ("Merchant", "You", or "Your").

By selecting the "I Agree" checkbox, clicking "Accept & Continue", and successfully verifying the OTP sent to your registered mobile number, you acknowledge that you have read, understood, and agreed to be legally bound by this Agreement and all related policies.

1. MERCHANT ACCEPTANCE
The Merchant confirms that:
- The information submitted during registration is true, complete, and accurate.
- The Merchant is legally authorized to operate the business.
- The Merchant agrees to comply with all applicable laws and regulations.
- The Merchant accepts this Agreement electronically through Checkbox Acceptance and OTP Verification.

2. KYC VERIFICATION
A1XPAY reserves the right to verify all KYC documents submitted by the Merchant. If any document is found to be false, forged, misleading, expired, or invalid, A1XPAY may suspend or permanently terminate the Merchant Account without prior notice.

3. PAYMENT PROCESSING
A1XPAY provides payment collection and related services. The Merchant shall use the services only for lawful business purposes. The Merchant shall not process transactions related to prohibited or illegal businesses.

4. PROHIBITED BUSINESSES
The Merchant shall not use A1XPAY for:
- Illegal Products or Services
- Gambling / Betting
- Adult Content
- Drugs or Narcotics
- Money Laundering / Terror Financing
- Fake Products / Copyright Infringement
- Cryptocurrency (unless expressly approved)
- Any business prohibited by applicable law.
Violation may result in immediate suspension or permanent termination.

5. SETTLEMENT
Settlements shall be processed according to the Settlement Policy applicable to the Merchant. A1XPAY reserves the right to hold, delay, suspend, or reverse settlements in case of Fraud Investigation, Chargebacks, Regulatory Requirement, Compliance Review, or High Risk Transactions.

6. REFUNDS
Refunds shall be processed in accordance with the Refund Policy. Applicable processing charges may be non-refundable unless otherwise specified.

7. CHARGEBACKS
The Merchant shall remain solely responsible for all customer disputes, chargebacks, penalties, fines, and associated costs. A1XPAY reserves the right to recover chargeback amounts from pending settlements or any future settlements.

8. FEES
The Merchant agrees to pay all applicable: Gateway Charges, Transaction Fees, GST, Refund Charges, Chargeback Charges, Annual or Monthly Fees (if applicable). Charges may be revised with prior notice.

9. COMPLIANCE
The Merchant agrees to comply with: RBI Guidelines, Applicable Indian Laws, AML Requirements, KYC Requirements, Data Protection Requirements.

10. SUSPENSION & TERMINATION
A1XPAY may suspend or terminate the Merchant Account at its sole discretion if: Fraud is suspected, Fake KYC is submitted, Excessive Chargebacks occur, Regulatory instructions are received, or this Agreement is violated.

11. LIMITATION OF LIABILITY
A1XPAY shall not be liable for: Business Loss, Loss of Profits, Indirect Damages, Network Failure, Bank Downtime, Third-party Service Failure, Force Majeure Events.

12. PRIVACY
Merchant information shall be handled according to the A1XPAY Privacy Policy. A1XPAY may share information with banks, regulators, law enforcement agencies, or payment partners where legally required.

13. MODIFICATION
A1XPAY reserves the right to modify this Agreement at any time. Updated versions shall become effective upon publication on the A1XPAY website or Merchant Dashboard.

14. GOVERNING LAW
This Agreement shall be governed by the laws of India. Any dispute shall be subject to the exclusive jurisdiction of the competent courts where A1XPAY's registered office is situated.

15. ELECTRONIC ACCEPTANCE
The Merchant expressly agrees that clicking "Accept & Continue", selecting the Agreement Checkbox, and successfully verifying the OTP constitutes the Merchant's valid electronic consent and legally binding acceptance of this Agreement.

The following acceptance records may be stored by A1XPAY: Merchant ID, Name, Mobile, Email, OTP Verification Status, Date & Time, IP Address, Browser & Device Information, Agreement Version, and Digital Acceptance Log.

By proceeding, the Merchant confirms that they have read, understood, and accepted this Agreement voluntarily and without any coercion.`;

/* ── T&C Modal ── */
const TermsModal = ({ onAccept, onClose }) => {
  const [scrolled, setScrolled] = useState(false);

  const handleScroll = (e) => {
    const el = e.target;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) setScrolled(true);
  };

  const handleDownload = () => {
    const blob = new Blob([AGREEMENT_TEXT], { type: 'text/plain;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = 'A1xPay-Merchant-Agreement-v1.0.txt'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={M.overlay}>
      <div style={M.modal}>
        <div style={M.header}>
          <div>
            <p style={{ margin: 0, fontSize: 11, color: '#6e6e86', textTransform: 'uppercase', letterSpacing: '.05em' }}>A1xPay</p>
            <h2 style={{ margin: '2px 0 0', fontSize: 16, fontWeight: 700, color: '#14142b' }}>Merchant Agreement</h2>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handleDownload} style={M.downloadBtn}>↓ Download</button>
            <button onClick={onClose} style={M.closeBtn}>✕</button>
          </div>
        </div>

        <div style={{ padding: '8px 20px', background: '#f1ebff', borderBottom: '1px solid #ddd6fe', fontSize: 12, color: '#7c3aed' }}>
          Effective Version: v1.0 · Please read the complete agreement before accepting.
        </div>

        {!scrolled && (
          <div style={{ padding: '6px 20px', background: '#FFFBEB', borderBottom: '1px solid #FDE68A', fontSize: 11, color: '#92400E' }}>
            Scroll to the bottom to enable the Accept button.
          </div>
        )}

        <div onScroll={handleScroll} style={M.content}>
          <pre style={{ margin: 0, fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#4a4a6a', lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {AGREEMENT_TEXT}
          </pre>
        </div>

        <div style={M.footer}>
          <button onClick={onClose} style={M.cancelBtn}>Cancel</button>
          <button
            onClick={onAccept}
            disabled={!scrolled}
            style={{ ...M.acceptBtn, opacity: scrolled ? 1 : 0.4, cursor: scrolled ? 'pointer' : 'not-allowed' }}
          >
            I Accept & Continue
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── OTP Modal ── */
const OtpModal = ({ phone, onVerified, onClose }) => {
  const [otp, setOtp]           = useState('');
  const [sending, setSending]   = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [sent, setSent]         = useState(false);
  const [error, setError]       = useState('');
  const [countdown, setCountdown] = useState(0);

  const maskedPhone = phone
    ? phone.toString().replace(/(\d{2})\d{6}(\d{2})/, '$1xxxxxxxx')
    : '**********';

  const startCountdown = () => {
    setCountdown(60);
    const t = setInterval(() => {
      setCountdown(c => { if (c <= 1) { clearInterval(t); return 0; } return c - 1; });
    }, 1000);
  };

  const sendOtp = async () => {
    setSending(true); setError('');
    try {
      await otpAPI.send(phone);
      setSent(true);
      startCountdown();
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to send OTP. Please try again.');
    } finally { setSending(false); }
  };

  const verifyOtp = async () => {
    if (otp.length !== 6) { setError('Please enter 6-digit OTP'); return; }
    setVerifying(true); setError('');
    try {
      await otpAPI.verify(phone, otp);
      onVerified();
    } catch (e) {
      setError(e?.response?.data?.error || 'Invalid OTP. Please try again.');
    } finally { setVerifying(false); }
  };

  return (
    <div style={M.overlay}>
      <div style={{ ...M.modal, maxWidth: 400 }}>
        <div style={M.header}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#14142b' }}>Verify Mobile Number</h2>
          <button onClick={onClose} style={M.closeBtn}>✕</button>
        </div>

        <div style={{ padding: 24 }}>
          <p style={{ margin: '0 0 20px', fontSize: 13, color: '#4a4a6a', textAlign: 'center' }}>
            {sent
              ? <>OTP sent to <strong>+91 {maskedPhone}</strong><br />Enter the 6-digit code below.</>
              : <>We will send a 6-digit OTP to<br /><strong>+91 {maskedPhone}</strong> to verify your number.</>
            }
          </p>

          {!sent ? (
            <button onClick={sendOtp} disabled={sending} style={{ ...M.acceptBtn, width: '100%' }}>
              {sending ? 'Sending OTP…' : 'Send OTP'}
            </button>
          ) : (
            <>
              <input
                type="tel" maxLength={6} placeholder="Enter 6-digit OTP"
                value={otp}
                onChange={e => { setOtp(e.target.value.replace(/\D/g, '')); setError(''); }}
                style={{
                  width: '100%', padding: '12px', fontSize: 22, fontWeight: 700,
                  textAlign: 'center', letterSpacing: '0.5em',
                  border: `2px solid ${error ? '#EF4444' : '#e8e8f1'}`,
                  borderRadius: 10, outline: 'none', boxSizing: 'border-box',
                  color: '#14142b', background: '#f9f9fc', marginBottom: 8,
                }}
                autoFocus
              />

              {error && <p style={{ margin: '0 0 12px', fontSize: 12, color: '#EF4444', textAlign: 'center' }}>{error}</p>}

              <button
                onClick={verifyOtp}
                disabled={verifying || otp.length !== 6}
                style={{ ...M.acceptBtn, width: '100%', marginBottom: 12, opacity: otp.length === 6 ? 1 : 0.5 }}
              >
                {verifying ? 'Verifying…' : 'Verify & Submit'}
              </button>

              <p style={{ margin: 0, fontSize: 12, color: '#6e6e86', textAlign: 'center' }}>
                {countdown > 0
                  ? `Resend OTP in ${countdown}s`
                  : <button onClick={sendOtp} disabled={sending} style={{ background: 'none', border: 'none', color: '#7c3aed', cursor: 'pointer', fontSize: 12, padding: 0 }}>
                      {sending ? 'Sending…' : 'Resend OTP'}
                    </button>
                }
              </p>
            </>
          )}

          {!sent && error && <p style={{ margin: '8px 0 0', fontSize: 12, color: '#EF4444', textAlign: 'center' }}>{error}</p>}
        </div>
      </div>
    </div>
  );
};

/* ── small helpers ── */
const Field = ({ label, icon: Icon, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    <label style={{ fontSize: 12, fontWeight: 600, color: C.body, display: 'flex', alignItems: 'center', gap: 5 }}>
      {Icon && <Icon size={13} strokeWidth={2} color={C.muted} />}
      {label}
    </label>
    {children}
  </div>
);

const inputStyle = {
  width: '100%', padding: '9px 12px',
  fontSize: 13, color: C.ink,
  background: C.bg, border: `1px solid ${C.border}`,
  borderRadius: C.radiusSm, outline: 'none',
  transition: 'border-color .15s',
  boxSizing: 'border-box',
};

const InfoRow = ({ label, value, badge }) => (
  <div style={{
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '10px 0', borderBottom: `1px solid ${C.borderSoft}`,
  }}>
    <span style={{ fontSize: 12, color: C.muted }}>{label}</span>
    {badge
      ? <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, ...badge }}>{value}</span>
      : <span style={{ fontSize: 13, fontWeight: 600, color: C.ink }}>{value || '—'}</span>
    }
  </div>
);

/* ── Main Component ── */
const UserProfile = () => {
  const { user, updateUser } = useAuth();
  const [loading, setLoading]   = useState(true);
  const [saving,  setSaving]    = useState(false);
  const [profileData, setProfileData] = useState({
    full_name: '', business_name: '', phone: '',
    address: '', city: '', state: '', pincode: '',
    gst_number: '', pan_number: '', aadhaar_number: '',
  });
  const [profileImage,  setProfileImage]  = useState(null);
  const [imagePreview,  setImagePreview]  = useState(null);
  const [focusedField,  setFocusedField]  = useState(null);
  const [bankDetails,   setBankDetails]   = useState(null);

  // T&C + OTP state
  const [agreeTerms,  setAgreeTerms]  = useState(false);
  const [showTerms,   setShowTerms]   = useState(false);
  const [showOtp,     setShowOtp]     = useState(false);
  const [termsError,  setTermsError]  = useState('');

  useEffect(() => { fetchProfile(); }, []);

  const fetchProfile = async () => {
    try {
      const response = await userAPI.getProfile();
      const { user: userData, profile } = response.data.data;
      if (profile) {
        setProfileData({
          full_name:      profile.full_name      || '',
          business_name:  profile.business_name  || '',
          phone:          userData.phone          || '',
          address:        profile.address         || '',
          city:           profile.city            || '',
          state:          profile.state           || '',
          pincode:        profile.pincode         || '',
          gst_number:     profile.gst_number      || '',
          pan_number:     profile.pan_number      || '',
          aadhaar_number: profile.aadhaar_number  || '',
        });
        setImagePreview(profile.profile_image);
      }
      if (userData) {
        setBankDetails({
          bank_holder_name: userData.bank_holder_name || null,
          bank_account_no:  userData.bank_account_no  || null,
          bank_ifsc:        userData.bank_ifsc         || null,
          bank_name:        userData.bank_name         || null,
          bank_branch:      userData.bank_branch       || null,
          account_type:     userData.account_type      || null,
        });
      }
    } catch {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => setProfileData(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2048000) { toast.error('Image must be under 2 MB'); return; }
    setProfileImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleTermsCheckbox = (e) => {
    if (e.target.checked) {
      setShowTerms(true);
    } else {
      setAgreeTerms(false);
    }
    setTermsError('');
  };

  const handleTermsAccept = () => {
    setAgreeTerms(true);
    setShowTerms(false);
  };

  // Step 1: form submit → validate T&C → show OTP modal
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreeTerms) {
      setTermsError('Please read and accept the Merchant Agreement before submitting.');
      return;
    }
    if (!profileData.phone || !/^[6-9]\d{9}$/.test(profileData.phone)) {
      toast.error('Please enter a valid 10-digit mobile number to receive the OTP.');
      return;
    }
    setShowOtp(true);
  };

  // Step 2: OTP verified → save profile
  const handleOtpVerified = async () => {
    setShowOtp(false);
    setSaving(true);
    try {
      const formData = new FormData();
      Object.keys(profileData).forEach(k => { if (profileData[k]) formData.append(k, profileData[k]); });
      if (profileImage) formData.append('profile_image', profileImage);
      await userAPI.updateProfile(formData);
      toast.success('KYC submitted successfully!');
      updateUser({ ...user, phone: profileData.phone });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const statusBadge = user?.account_status === 'active'
    ? { background: C.successSoft, color: '#065F46' }
    : { background: C.dangerSoft,  color: '#991B1B' };

  return (
    <div className="dashboard-layout">
      <Sidebar role="user" />

      <div className="dashboard-content profile-page-content" style={{ background: C.bg, minHeight: '100vh' }}>

        {/* page header */}
        <div style={{ marginBottom: 28 }}>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: C.ink, margin: 0 }}>My Profile / KYC</h2>
          <p  style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>Fill your details and verify to complete KYC</p>
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '80px 0' }}>
            <Loader2 size={32} color={C.primary} style={{ animation: 'spin 1s linear infinite' }} />
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="profile-main-grid">

              {/* left column */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

                {/* Avatar card */}
                <div style={{
                  background: C.surface, border: `1px solid ${C.border}`,
                  borderRadius: C.radius, padding: 24, textAlign: 'center',
                  boxShadow: '0 1px 3px rgba(20,20,43,.04)',
                }}>
                  <p style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 18 }}>
                    Profile Picture
                  </p>

                  <div style={{ position: 'relative', display: 'inline-block', marginBottom: 14 }}>
                    <div style={{
                      width: 96, height: 96, borderRadius: '50%',
                      background: imagePreview ? 'transparent' : C.primarySoft,
                      border: `3px solid ${C.primaryTint}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      overflow: 'hidden',
                    }}>
                      {imagePreview
                        ? <img src={imagePreview} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <User size={40} color={C.primary} strokeWidth={1.5} />
                      }
                    </div>

                    <label htmlFor="profileImageInput" style={{
                      position: 'absolute', bottom: 0, right: 0,
                      width: 28, height: 28, borderRadius: '50%',
                      background: C.primary, color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', border: `2px solid ${C.surface}`,
                    }}>
                      <Camera size={13} strokeWidth={2} />
                      <input
                        type="file" id="profileImageInput"
                        accept="image/jpeg,image/png,image/jpg"
                        onChange={handleImageChange}
                        style={{ display: 'none' }}
                      />
                    </label>
                  </div>

                  <p style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>{profileData.full_name || 'Your Name'}</p>
                  <p style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>JPG, PNG • Max 2 MB</p>
                </div>

                {/* Account info */}
                <div style={{
                  background: C.surface, border: `1px solid ${C.border}`,
                  borderRadius: C.radius, padding: '20px 22px',
                  boxShadow: '0 1px 3px rgba(20,20,43,.04)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                    <ShieldCheck size={15} color={C.primary} strokeWidth={2} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: C.ink, textTransform: 'uppercase', letterSpacing: '.06em' }}>
                      Account Information
                    </span>
                  </div>

                  <InfoRow label="Username" value={user?.username} />
                  <InfoRow label="Email"    value={user?.email} />
                  <InfoRow
                    label="Role"
                    value={(user?.role || 'user').toUpperCase()}
                    badge={{ background: C.primarySoft, color: C.primary }}
                  />
                  <InfoRow
                    label="Status"
                    value={(user?.account_status || 'inactive').toUpperCase()}
                    badge={statusBadge}
                  />
                </div>
              </div>

              {/* right column */}
              <div style={{
                background: C.surface, border: `1px solid ${C.border}`,
                borderRadius: C.radius, padding: '24px 28px',
                boxShadow: '0 1px 3px rgba(20,20,43,.04)',
              }}>
                <p style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 22 }}>
                  Personal Information
                </p>

                <div className="profile-fields-grid">

                  <Field label="Full Name *" icon={User}>
                    <input
                      style={{ ...inputStyle, borderColor: focusedField === 'full_name' ? C.primary : C.border }}
                      type="text" name="full_name" value={profileData.full_name}
                      onChange={handleInputChange}
                      onFocus={() => setFocusedField('full_name')}
                      onBlur={() => setFocusedField(null)}
                      placeholder="Enter your full name" required
                    />
                  </Field>

                  <Field label="Business Name" icon={Building2}>
                    <input
                      style={{ ...inputStyle, borderColor: focusedField === 'business_name' ? C.primary : C.border }}
                      type="text" name="business_name" value={profileData.business_name}
                      onChange={handleInputChange}
                      onFocus={() => setFocusedField('business_name')}
                      onBlur={() => setFocusedField(null)}
                      placeholder="Enter business name"
                    />
                  </Field>

                  <Field label="Phone Number *" icon={Phone}>
                    <input
                      style={{ ...inputStyle, borderColor: focusedField === 'phone' ? C.primary : C.border }}
                      type="tel" name="phone" value={profileData.phone}
                      onChange={handleInputChange}
                      onFocus={() => setFocusedField('phone')}
                      onBlur={() => setFocusedField(null)}
                      placeholder="10-digit mobile number" required
                    />
                    <small style={{ fontSize: 11, color: C.muted }}>OTP will be sent to this number</small>
                  </Field>

                  <Field label="PAN Number" icon={CreditCard}>
                    <input
                      style={{ ...inputStyle, borderColor: focusedField === 'pan_number' ? C.primary : C.border, textTransform: 'uppercase' }}
                      type="text" name="pan_number" value={profileData.pan_number}
                      onChange={handleInputChange}
                      onFocus={() => setFocusedField('pan_number')}
                      onBlur={() => setFocusedField(null)}
                      placeholder="ABCDE1234F" maxLength="10"
                    />
                  </Field>

                  <Field label="Aadhaar Number" icon={CreditCard}>
                    <input
                      style={{ ...inputStyle, borderColor: focusedField === 'aadhaar_number' ? C.primary : C.border }}
                      type="text" name="aadhaar_number" value={profileData.aadhaar_number}
                      onChange={handleInputChange}
                      onFocus={() => setFocusedField('aadhaar_number')}
                      onBlur={() => setFocusedField(null)}
                      placeholder="1234 5678 9012" maxLength="12"
                    />
                  </Field>

                  <Field label="GST Number (optional)" icon={CreditCard}>
                    <input
                      style={{ ...inputStyle, borderColor: focusedField === 'gst_number' ? C.primary : C.border, textTransform: 'uppercase' }}
                      type="text" name="gst_number" value={profileData.gst_number}
                      onChange={handleInputChange}
                      onFocus={() => setFocusedField('gst_number')}
                      onBlur={() => setFocusedField(null)}
                      placeholder="22AAAAA0000A1Z5" maxLength="15"
                    />
                  </Field>

                  {/* Address — full width */}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <Field label="Address *" icon={MapPin}>
                      <textarea
                        style={{ ...inputStyle, resize: 'vertical', minHeight: 72, borderColor: focusedField === 'address' ? C.primary : C.border }}
                        name="address" value={profileData.address}
                        onChange={handleInputChange}
                        onFocus={() => setFocusedField('address')}
                        onBlur={() => setFocusedField(null)}
                        placeholder="Enter your complete address" rows="2" required
                      />
                    </Field>
                  </div>

                  <Field label="City *">
                    <input
                      style={{ ...inputStyle, borderColor: focusedField === 'city' ? C.primary : C.border }}
                      type="text" name="city" value={profileData.city}
                      onChange={handleInputChange}
                      onFocus={() => setFocusedField('city')}
                      onBlur={() => setFocusedField(null)}
                      placeholder="City" required
                    />
                  </Field>

                  <Field label="State *">
                    <input
                      style={{ ...inputStyle, borderColor: focusedField === 'state' ? C.primary : C.border }}
                      type="text" name="state" value={profileData.state}
                      onChange={handleInputChange}
                      onFocus={() => setFocusedField('state')}
                      onBlur={() => setFocusedField(null)}
                      placeholder="State" required
                    />
                  </Field>

                  <Field label="Pincode *">
                    <input
                      style={{ ...inputStyle, borderColor: focusedField === 'pincode' ? C.primary : C.border }}
                      type="text" name="pincode" value={profileData.pincode}
                      onChange={handleInputChange}
                      onFocus={() => setFocusedField('pincode')}
                      onBlur={() => setFocusedField(null)}
                      placeholder="6-digit pincode" maxLength="6" required
                    />
                  </Field>
                </div>

                {/* ── T&C Checkbox ── */}
                <div style={{
                  marginTop: 24, padding: '16px 18px',
                  background: agreeTerms ? '#F0FDF4' : '#FFFBEB',
                  border: `1px solid ${agreeTerms ? '#BBF7D0' : termsError ? '#FECACA' : '#FDE68A'}`,
                  borderRadius: C.radiusSm,
                }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={agreeTerms}
                      onChange={handleTermsCheckbox}
                      style={{ accentColor: C.primary, marginTop: 2, flexShrink: 0, width: 16, height: 16 }}
                    />
                    <span style={{ fontSize: 13, color: C.ink, lineHeight: 1.55 }}>
                      I have read and agree to the{' '}
                      <span
                        onClick={() => setShowTerms(true)}
                        style={{ color: C.primary, cursor: 'pointer', textDecoration: 'underline', fontWeight: 600 }}
                      >
                        A1xPay Merchant Agreement & Terms
                      </span>
                      . I confirm that all details submitted are accurate and I consent to OTP-based verification.
                    </span>
                  </label>

                  {agreeTerms && (
                    <div style={{ marginTop: 8, fontSize: 12, color: '#15803D', display: 'flex', alignItems: 'center', gap: 5, paddingLeft: 26 }}>
                      ✓ Agreement accepted · OTP verification will be required on submit
                    </div>
                  )}

                  {termsError && (
                    <div style={{ marginTop: 8, fontSize: 12, color: C.danger, paddingLeft: 26 }}>{termsError}</div>
                  )}
                </div>

                {/* Save button */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
                  <button
                    type="submit"
                    disabled={saving}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                      padding: '10px 24px', borderRadius: C.radiusSm,
                      background: saving ? C.muted : C.primary,
                      color: '#fff', fontWeight: 700, fontSize: 13,
                      border: 'none', cursor: saving ? 'not-allowed' : 'pointer',
                      transition: 'background .15s',
                    }}
                  >
                    {saving
                      ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Saving…</>
                      : <><Save size={14} strokeWidth={2} /> Submit KYC</>
                    }
                  </button>
                </div>
              </div>
            </div>
          </form>
        )}

        {/* Bank Details — read-only */}
        {!loading && (
          <div style={{
            background: C.surface, border: `1px solid ${C.border}`, borderRadius: C.radius,
            padding: '24px 28px', marginTop: 22, boxShadow: '0 1px 3px rgba(20,20,43,.04)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <div>
                <p style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.07em', margin: 0 }}>
                  Bank Account Details
                </p>
                <p style={{ fontSize: 12, color: C.muted, margin: '4px 0 0' }}>
                  To update bank details, please contact support.
                </p>
              </div>
              <span style={{
                fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 20,
                background: C.primarySoft, color: C.primary,
              }}>Read Only</span>
            </div>

            {(bankDetails?.bank_account_no || bankDetails?.bank_holder_name) ? (
              <div className="profile-bank-grid">
                {[
                  ['Account Holder', bankDetails?.bank_holder_name],
                  ['Account Number', bankDetails?.bank_account_no],
                  ['IFSC Code',      bankDetails?.bank_ifsc],
                  ['Bank Name',      bankDetails?.bank_name],
                  ['Branch',         bankDetails?.bank_branch],
                  ['Account Type',   bankDetails?.account_type],
                ].map(([label, val]) => (
                  <div key={label}>
                    <div style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 3 }}>{label}</div>
                    <div style={{
                      fontSize: 13, fontWeight: 600, color: C.ink,
                      background: C.bg, border: `1px solid ${C.border}`,
                      borderRadius: 8, padding: '8px 12px',
                    }}>{val || '—'}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '24px 0', color: C.muted, fontSize: 13 }}>
                No bank details on file. Contact support to add your bank account.
              </div>
            )}
          </div>
        )}
      </div>

      {/* T&C Modal */}
      {showTerms && (
        <TermsModal
          onAccept={handleTermsAccept}
          onClose={() => setShowTerms(false)}
        />
      )}

      {/* OTP Modal */}
      {showOtp && (
        <OtpModal
          phone={profileData.phone}
          onVerified={handleOtpVerified}
          onClose={() => setShowOtp(false)}
        />
      )}

      <style>{`
        @keyframes spin{to{transform:rotate(360deg)}}

        .profile-page-content { padding: 28px 28px 40px; }

        .profile-main-grid {
          display: grid;
          grid-template-columns: 300px 1fr;
          gap: 22px;
          align-items: start;
        }

        .profile-fields-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 18px 20px;
        }

        .profile-bank-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 14px 20px;
        }

        @media (max-width: 900px) {
          .profile-main-grid { grid-template-columns: 1fr; }
          .profile-bank-grid { grid-template-columns: 1fr 1fr; }
        }

        @media (max-width: 600px) {
          .profile-page-content { padding: 16px 14px 32px; }
          .profile-fields-grid  { grid-template-columns: 1fr; }
          .profile-bank-grid    { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
};

/* ── Modal styles ── */
const M = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)',
    zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
  },
  modal: {
    background: '#fff', borderRadius: 16, width: '100%', maxWidth: 640,
    maxHeight: '90vh', display: 'flex', flexDirection: 'column',
    boxShadow: '0 25px 80px rgba(0,0,0,.3)', overflow: 'hidden',
  },
  header: {
    padding: '16px 20px', borderBottom: '1px solid #e8e8f1',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0,
  },
  content: { flex: 1, overflowY: 'auto', padding: '20px 24px' },
  footer: {
    padding: '14px 20px', borderTop: '1px solid #e8e8f1',
    display: 'flex', gap: 10, justifyContent: 'flex-end', flexShrink: 0,
  },
  downloadBtn: {
    padding: '7px 14px', fontSize: 12, borderRadius: 8,
    border: '1px solid #e8e8f1', background: '#f9f9fc', color: '#4a4a6a',
    cursor: 'pointer', fontWeight: 600,
  },
  closeBtn: {
    padding: '7px 12px', fontSize: 14, borderRadius: 8,
    border: '1px solid #e8e8f1', background: '#f9f9fc', color: '#4a4a6a', cursor: 'pointer',
  },
  cancelBtn: {
    padding: '10px 20px', fontSize: 13, borderRadius: 8,
    border: '1px solid #e8e8f1', background: '#f9f9fc', color: '#4a4a6a', cursor: 'pointer',
  },
  acceptBtn: {
    padding: '10px 24px', fontSize: 13, fontWeight: 700, borderRadius: 8,
    border: 'none', background: '#14142b', color: '#fff', cursor: 'pointer',
  },
};

export default UserProfile;
